import { gql } from '@apollo/client/core';

import LocationService from '@/services/LocationService';
import { executeQuery } from '@/components/commons/utils.js';

const PING_QUERY = gql`
  query PING($postalCode: String) {
    customerSession {
      sessionId
      email
      firstName
      lastName
      verified
      expired
      registered
      isTrade
      cart {
        cartItems {
          quantity
        }
      }
      token
    }
    office
    getLocationByIP {
      location {
        country
        currency
        region {
          id
          name
          code
        }
        city
        postalCode
        accuracy
      }
    }
    getLocationByPostalCode(postalCode: $postalCode) {
      location {
        country
        currency
        region {
          id
          name
          code
        }
        city
        postalCode
        accuracy
      }
    }
  }
`;

const GET_LOCATION_BY_POSTALCODE_QUERY = gql`
  query GET_LOCATION_BY_POSTALCODE($postalCode: String) {
    getLocationByPostalCode(postalCode: $postalCode) {
      location {
        country
        currency
        region {
          id
          name
          code
        }
        city
        postalCode
        accuracy
      }
    }
  }
`;

export default class SessionService {
  // geoLocation -> "detected location (full object)"
  // zipLocation -> "resolved location data for the given (possibly user provided) postal code (full object)"
  // preferredLocation -> zipLocation with "fallback" (never empty, safe to use location)

  static async ping(zip) {
    try {
      const promises = [];
      let zipLocation = null;

      /**
       * first, consider user-inputted zip
       * second, consider current LocationService.getZipLocation()'s zipcode if manual input flag is true (means previously inputted zip)
       * failing both, we go with null so it won't query GET_LOCATION_BY_POSTALCODE_QUERY
       * **/

      const zipCode =
        zip?.trim() || (LocationService.getZipLocation() && LocationService.getIsLocationManual() ? LocationService.getZipLocation()?.zipcode : null);

      promises.push(
        executeQuery(PING_QUERY, {
          postalCode: LocationService.getCdnProvidedPostalCode(),
        }),
      );

      if (zipCode) {
        promises.push(
          executeQuery(GET_LOCATION_BY_POSTALCODE_QUERY, {
            postalCode: zipCode,
          }),
        );
      }

      const data = await Promise.all(promises);

      const pingResult = data[0];
      const zipResult = data?.[1];

      const { customerSession, office } = pingResult;

      // blacksheepwall || cdn-provided postal code
      const geoLocation = pingResult.getLocationByIP?.location ? pingResult.getLocationByIP : pingResult.getLocationByPostalCode;

      if (!pingResult.getLocationByIP?.location && pingResult.getLocationByPostalCode?.location) LocationService.setIsGeoLocationFromCdn(true);

      /**
       * At this point, we decide which location to use for zipLocation
       * Result from query GET_LOCATION_BY_POSTALCODE_QUERY takes priority (means respecting user-inputted zip)
       * Then we consider if no geoLocation (BSW and CDN both no result) or this page reload is due to a country switch (ie user in Canada and switched to US site),
       *      we use the default location of the selected country (based on cooke currency id)
       * if site is same country as geoLocation, use geoLocation value
       */

      if (zip) {
        zipLocation = zipResult.getLocationByPostalCode?.location ? zipResult.getLocationByPostalCode : null;
      } else if (!geoLocation?.location || LocationService.getCookieCurrency() !== (geoLocation?.location?.currency === 'CAD' ? 2 : 1)) {
        zipLocation = LocationService.getDefaultLocation();
      } else {
        zipLocation = geoLocation;
      }

      return {
        user: {
          ...customerSession,
        },
        customerToken: customerSession?.token,
        office,
        geoLocation: geoLocation?.location
          ? {
              accuracyRadius: geoLocation.location.accuracy,
              countryId: geoLocation.location.country === 'CA' ? 1 : 2,
              countryCode: geoLocation.location.country,
              regionId: geoLocation.location.region?.id,
              regionCode: geoLocation.location.region?.code,
              city: geoLocation.location.city,
              currencyId: geoLocation.location.currency === 'CAD' ? 2 : 1,
              zipcode: geoLocation.location.postalCode,
            }
          : null,
        zipLocation: zipLocation?.location
          ? {
              accuracyRadius: zipLocation.location.accuracy,
              countryId: zipLocation.location.country === 'CA' ? 1 : 2,
              countryCode: zipLocation.location.country,
              regionId: zipLocation.location.region?.id,
              regionCode: zipLocation.location.region?.code,
              city: zipLocation.location.city,
              currencyId: zipLocation.location.currency === 'CAD' ? 2 : 1,
              zipcode: zipLocation.location.postalCode,
            }
          : zipLocation,
      };
    } catch (error) {
      console.error(error);
      return {
        error,
      };
    }
  }
}
